import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { LangProvider } from "./i18n";
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import TwoFactor from "./pages/TwoFactor";
import AcceptInvite from "./pages/AcceptInvite";
import MicrosoftComplete from "./pages/MicrosoftComplete";
import WorkspaceSelect from "./pages/WorkspaceSelect";
import Dashboard from "./pages/Dashboard";
import Companies from "./pages/Companies";
import Hospitals from "./pages/Hospitals";
import Items from "./pages/Items";
import Purchases from "./pages/Purchases";
import PurchaseForm from "./pages/PurchaseForm";
import Sales from "./pages/Sales";
import SalesForm from "./pages/SalesForm";
import Quotations from "./pages/Quotations";
import QuotationForm from "./pages/QuotationForm";
import Inventory from "./pages/Inventory";
import Payments from "./pages/Payments";
import Tenders from "./pages/Tenders";
import Reports from "./pages/Reports";
import FinanceCenter from "./pages/FinanceCenter";
import FinanceHub from "./pages/FinanceHub";
import InvoicePrint from "./pages/InvoicePrint";
import Settings from "./pages/Settings";
import Admin from "./pages/Admin";
import LoginActivity from "./pages/LoginActivity";
import EWayBills from "./pages/EWayBills";
import Returns from "./pages/Returns";
import TenderDetail from "./pages/TenderDetail";
import Letters from "./pages/Letters";
import LetterForm from "./pages/LetterForm";
import VerifyDocument from "./pages/VerifyDocument";
import Parties from "./pages/Parties";
import StockItems from "./pages/StockItems";
import DispatchDocuments from "./pages/DispatchDocuments";
import TenderWorkspace from "./pages/TenderWorkspace";
import DesktopUpdater from "./components/DesktopUpdater";

function isMobileOrTablet() {
  const ua = navigator.userAgent || "";
  return /Android|iPhone|iPad|iPod|Mobile|Tablet|Kindle|Silk|BlackBerry|Opera Mini/i.test(ua)
    || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
    || navigator.userAgentData?.mobile === true;
}

function App() {
  const isPublicVerification = window.location.pathname.startsWith("/verify/");
  if (isMobileOrTablet() && !isPublicVerification) return (
    <main className="min-h-screen bg-slate-950 text-white flex items-center justify-center px-6">
      <div className="max-w-md text-center space-y-3">
        <h1 className="text-2xl font-bold">SGP Office OS</h1>
        <p className="text-slate-300">This workspace is available on desktop and laptop computers only. Open it on your office computer to continue.</p>
      </div>
    </main>
  );
  return (
    <AuthProvider>
      <LangProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/login/verify" element={<TwoFactor />} />
            <Route path="/accept-invite" element={<AcceptInvite />} />
            <Route path="/auth/microsoft/complete" element={<MicrosoftComplete />} />
            <Route path="/workspace" element={<ProtectedRoute requireWorkspace={false}><WorkspaceSelect /></ProtectedRoute>} />
            <Route path="/verify/:kind/:id" element={<VerifyDocument />} />
            <Route path="/print/:kind/:id" element={<ProtectedRoute><InvoicePrint /></ProtectedRoute>} />
            <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route index element={<Dashboard />} />
              <Route path="companies" element={<Companies />} />
              <Route path="hospitals" element={<Hospitals />} />
              <Route path="parties/:type" element={<Parties />} />
              <Route path="stock" element={<StockItems />} />
              <Route path="proformas" element={<DispatchDocuments kind="proformas" />} />
              <Route path="challans" element={<DispatchDocuments kind="challans" />} />
              <Route path="items" element={<Items />} />
              <Route path="purchases" element={<Purchases />} />
              <Route path="purchases/new" element={<PurchaseForm />} />
              <Route path="purchases/:id/edit" element={<PurchaseForm />} />
              <Route path="sales" element={<Sales />} />
              <Route path="sales/new" element={<SalesForm />} />
              <Route path="sales/:id/edit" element={<SalesForm />} />
              <Route path="quotations" element={<Quotations />} />
              <Route path="quotations/new" element={<QuotationForm />} />
              <Route path="quotations/:id/edit" element={<QuotationForm />} />
              <Route path="inventory" element={<Inventory />} />
              <Route path="payments" element={<Payments />} />
              <Route path="tenders" element={<Tenders />} />
              <Route path="tenders/dashboard" element={<TenderWorkspace section="dashboard" />} />
              <Route path="tenders/security" element={<TenderWorkspace section="security" />} />
              <Route path="tenders/documents" element={<TenderWorkspace section="documents" />} />
              <Route path="tenders/contracts" element={<TenderWorkspace section="contracts" />} />
              <Route path="tenders/reports" element={<TenderWorkspace section="reports" />} />
              <Route path="tenders/:id" element={<TenderDetail />} />
              <Route path="reports" element={<Reports />} />
              <Route path="finance" element={<FinanceHub />} />
              <Route path="settings" element={<Settings />} />
              <Route path="admin" element={<Admin />} />
              <Route path="login-activity" element={<LoginActivity />} />
              <Route path="eway" element={<EWayBills />} />
              <Route path="returns" element={<Returns />} />
              <Route path="letters" element={<Letters />} />
              <Route path="letters/new" element={<LetterForm />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
        <DesktopUpdater />
      </LangProvider>
    </AuthProvider>
  );
}

export default App;
